import {
  Controller,
  Control,
  UseFormGetValues,
  UseFormSetValue,
  FieldErrors,
} from 'react-hook-form';
import isEqual from 'lodash.isequal';
import { Concentration, ConcentrationFormData } from 'interfaces/concentration';
import Select from 'react-select';
import Checkbox from 'components/UI/Checkbox/Checkbox';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import DecimalInput from 'components/UI/DecimalInput/DecimalInput';
import ReactTooltip from 'react-tooltip';
import { calculateVariablePrecision, validatePrecision } from 'helpers/drugs';
import {
  getLoadingBolusLimits,
  getLoadingBolusUnits,
  wrongLimits,
} from 'helpers/concentrations';
import { MINUTES, SECONDS } from 'constants/drugs';

/**
 * @description Props for the LoadingDoseLimits component
 * @typedef {Object} LoadingDoseLimitsProps
 */
type LoadingDoseLimitsProps = {
  /** The concentration record being edited */
  record: Concentration;
  /** React Hook Form control object */
  control: Control<ConcentrationFormData, any>;
  /** Function to get form values */
  getValues: UseFormGetValues<ConcentrationFormData>;
  /** Function to set form values */
  setValue: UseFormSetValue<ConcentrationFormData>;
  /** Form validation errors */
  errors: FieldErrors<ConcentrationFormData>;
  /** Whether the concentration is weight-based */
  watchWeightBased: boolean;
  /** Lower hard limit for loading amount */
  watchLoadAmountLHL: string | number | null | undefined;
  /** Lower soft limit for loading amount */
  watchLoadAmountLSL: string | number | null | undefined;
  /** Upper soft limit for loading amount */
  watchLoadAmountUSL: string | number | null | undefined;
  /** Upper hard limit for loading amount */
  watchLoadAmountUHL: string | number | null | undefined;
  /** Lower hard limit for loading time */
  watchLoadTimeLHL: string | number | null | undefined;
  /** Lower soft limit for loading time */
  watchLoadTimeLSL: string | number | null | undefined;
  /** Upper soft limit for loading time */
  watchLoadTimeUSL: string | number | null | undefined;
  /** Upper hard limit for loading time */
  watchLoadTimeUHL: string | number | null | undefined;
  /** Whether loading is allowed */
  watchAllowLoad: boolean;
  /** Whether bolus is allowed */
  watchAllowBolus: boolean;
  /** Dose mode unit */
  watchDoseModeUnit:
    | {
        label: string;
        value: string;
      }
    | null
    | undefined;
  /** Dose mode time */
  watchDoseModeTime:
    | {
        label: string;
        value: string;
      }
    | null
    | undefined;
  /** Loading time units */
  watchLoadTimeUnits:
    | {
        label: string;
        value: string;
      }
    | null
    | undefined;
  /** Loading amount units */
  watchLoadAmountUnits:
    | {
        label: string;
        value: string;
      }
    | null
    | undefined;
  /** Bolus time units */
  watchBolusTimeUnits:
    | {
        label: string;
        value: string;
      }
    | null
    | undefined;
  /** Bolus amount units */
  watchBolusAmountUnits:
    | {
        label: string;
        value: string;
      }
    | null
    | undefined;
  /** Lower hard limit for concentration */
  watchConcentrationLHL: string | number | null | undefined;
  /** Upper hard limit for concentration */
  watchConcentrationUHL: string | number | null | undefined;
  /** Whether the user can manage this concentration */
  canManage: boolean;
};

/**
 * @description Component for configuring loading dose limits (amount and time with units).
 * Validates limits order (LHL < LSL < USL < UHL) and allowed ranges.
 *
 * @SADtrace SAD-003-4.1, SAD-003-4.2, SAD-003-4.3
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @param {LoadingDoseLimitsProps} props - Component props
 *
 * @returns {JSX.Element | null} Rendered component or null if no units available
 */
const LoadingDoseLimits: FC<LoadingDoseLimitsProps> = ({
  record,
  control,
  getValues,
  setValue,
  errors,
  watchAllowLoad,
  watchLoadAmountLHL,
  watchLoadAmountLSL,
  watchLoadAmountUHL,
  watchLoadAmountUSL,
  watchLoadAmountUnits,
  watchLoadTimeLHL,
  watchLoadTimeLSL,
  watchLoadTimeUHL,
  watchLoadTimeUSL,
  watchLoadTimeUnits,
  watchWeightBased,
  watchAllowBolus,
  watchBolusAmountUnits,
  watchBolusTimeUnits,
  watchDoseModeUnit,
  watchDoseModeTime,
  watchConcentrationLHL,
  watchConcentrationUHL,
  canManage,
}) => {
  const { t } = useTranslation();

  const precisionLoadAmountLHL = useMemo<any>(
    () => calculateVariablePrecision(watchLoadAmountLHL),
    [watchLoadAmountLHL],
  );
  const precisionLoadAmountLSL = useMemo<any>(
    () => calculateVariablePrecision(watchLoadAmountLSL),
    [watchLoadAmountLSL],
  );
  const precisionLoadAmountUSL = useMemo<any>(
    () => calculateVariablePrecision(watchLoadAmountUSL),
    [watchLoadAmountUSL],
  );
  const precisionLoadAmountUHL = useMemo<any>(
    () => calculateVariablePrecision(watchLoadAmountUHL),
    [watchLoadAmountUHL],
  );

  const loadTimeLimits: { min: number; max: number } = useMemo<any>(() => {
    if (!watchLoadTimeUnits) {
      return {
        min: 0,
        max: 0,
      };
    }

    switch (watchLoadTimeUnits.value) {
      case MINUTES:
        return { min: 1, max: 60 };
      case SECONDS:
        return { min: 30, max: 90 };
    }
  }, [watchLoadTimeUnits]);

  // Get loading and bolus units
  const loadingBolusUnits = useMemo(() => {
    const doseModeUnit = record.is_complete
      ? record.dose_mode_unit
      : watchDoseModeUnit?.value;
    const doseModeTime = record.is_complete
      ? record.dose_mode_time
      : watchDoseModeTime?.value;

    return getLoadingBolusUnits(
      record,
      watchWeightBased,
      doseModeUnit,
      doseModeTime,
    );
  }, [watchWeightBased, record, watchDoseModeUnit, watchDoseModeTime]);

  // get loading and bolus limits
  const loadingBolusLimits = useMemo(() => {
    return getLoadingBolusLimits(
      record,
      watchLoadAmountUnits,
      watchConcentrationLHL,
      watchConcentrationUHL,
    );
  }, [
    watchLoadAmountUnits,
    record,
    watchConcentrationLHL,
    watchConcentrationUHL,
  ]);

  /**
   * @description Handles changes to the loading amount units
   * Resets amount limits when units change and syncs with bolus amount units if needed
   *
   * @param {Object|null|undefined} value - The new units value
   */
  const handleLoadAmountsChange = (
    value:
      | {
          label: string;
          value: string;
        }
      | null
      | undefined,
  ) => {
    // v8 ignore start -- WAIVER(THIRDPARTY): unit-change reset handler reachable only via react-select onChange; the uncovered branch combinations (clearing or re-selecting an identical unit, divergent paired-unit state) require multi-step react-select menu interactions that are unreliable in jsdom; reset behavior is verified by ConcentrationForm tests and E2E
    if (
      (!!value && !watchLoadAmountUnits) ||
      (!value && !!watchLoadAmountUnits) ||
      !isEqual(value, watchLoadAmountUnits)
    ) {
      setValue('load_amount_lower_hard_limit', '');
      setValue('load_amount_lower_soft_limit', '');
      setValue('load_amount_upper_soft_limit', '');
      setValue('load_amount_upper_hard_limit', '');
    }

    if (watchAllowBolus) {
      if (!isEqual(value, watchBolusAmountUnits)) {
        setValue('bolus_amount_units', value, {
          shouldValidate: true,
        });
        setValue('bolus_amount_lower_hard_limit', '');
        setValue('bolus_amount_lower_soft_limit', '');
        setValue('bolus_amount_upper_soft_limit', '');
        setValue('bolus_amount_upper_hard_limit', '');
      }
    } else {
      if (
        (!!value && !watchBolusAmountUnits) ||
        (!value && !!watchBolusAmountUnits)
      ) {
        setValue('bolus_amount_units', value, {
          shouldValidate: true,
        });
        setValue('bolus_amount_lower_hard_limit', '');
        setValue('bolus_amount_lower_soft_limit', '');
        setValue('bolus_amount_upper_soft_limit', '');
        setValue('bolus_amount_upper_hard_limit', '');
      }
    }
    // v8 ignore stop -- WAIVER(THIRDPARTY)
  };

  /**
   * @description Handles changes to the loading time units
   * Resets time limits when units change and syncs with bolus time units if needed
   *
   * @param {Object|null|undefined} value - The new units value
   */
  const handleLoadTimeChange = (
    value:
      | {
          label: string;
          value: string;
        }
      | null
      | undefined,
  ) => {
    // v8 ignore start -- WAIVER(THIRDPARTY): unit-change reset handler reachable only via react-select onChange; the uncovered branch combinations (clearing or re-selecting an identical unit, divergent paired-unit state) require multi-step react-select menu interactions that are unreliable in jsdom; reset behavior is verified by ConcentrationForm tests and E2E
    if (
      (!!value && !watchLoadTimeUnits) ||
      (!value && !!watchLoadTimeUnits) ||
      !isEqual(value, watchLoadTimeUnits)
    ) {
      setValue('load_time_lower_hard_limit', '');
      setValue('load_time_lower_soft_limit', '');
      setValue('load_time_upper_soft_limit', '');
      setValue('load_time_upper_hard_limit', '');
    }

    if (watchAllowBolus) {
      if (!isEqual(value, watchBolusTimeUnits)) {
        setValue('bolus_time_units', value, {
          shouldValidate: true,
        });
        setValue('bolus_time_lower_hard_limit', '');
        setValue('bolus_time_lower_soft_limit', '');
        setValue('bolus_time_upper_soft_limit', '');
        setValue('bolus_time_upper_hard_limit', '');
      }
    } else {
      if (
        (!!value && !watchBolusTimeUnits) ||
        (!value && !!watchBolusTimeUnits)
      ) {
        setValue('bolus_time_units', value, {
          shouldValidate: true,
        });
        setValue('bolus_time_lower_hard_limit', '');
        setValue('bolus_time_lower_soft_limit', '');
        setValue('bolus_time_upper_soft_limit', '');
        setValue('bolus_time_upper_hard_limit', '');
      }
    }
    // v8 ignore stop -- WAIVER(THIRDPARTY)
  };

  if (!Array.isArray(loadingBolusUnits) || !loadingBolusUnits.length) {
    return null;
  }

  return (
    <div className="position-relative row">
      {/* v8 ignore start -- WAIVER(MICRO): trivial read-only render branch that only toggles a decorative disabled-overlay div for users without manage permission; no assertable logic beyond element presence */}
      {!canManage && (
        <div className="device-concentration--disabled-item"></div>
      )}
      {/* v8 ignore stop -- WAIVER(MICRO) */}

      <div className="device-concentration-form__allow-load--wrapper">
        <div className="row flex-nowrap">
          <div className="device-concentration-form__label justify-content-between">
            <span>{t('device.allow_load')}</span>
            {watchAllowLoad && <span>{t('device.amount')}</span>}
          </div>

          {/* Lower hard limit */}
          <div className="device-concentration__box-input">
            {watchAllowLoad && watchLoadAmountUnits && (
              <>
                <Controller
                  control={control}
                  name="load_amount_lower_hard_limit"
                  // v8 ignore start -- WAIVER(THIRDPARTY): react-hook-form validate rules executed inside the RHF Controller config; the uncovered cross-limit order ternaries (LHL/LSL/USL/UHL comparisons) and input-shape guards need full-form value matrices, exercised representatively by ConcentrationForm form-level tests and the E2E suite
                  rules={{
                    min: loadingBolusLimits.min,
                    max: loadingBolusLimits.max,
                    validate: {
                      increasingOrder: (value) => {
                        if (!value || isNaN(Number(value))) return true;

                        const currentValue = Number(value);
                        let isValid = true;

                        const [LSL, USL, UHL] = getValues([
                          'load_amount_lower_soft_limit',
                          'load_amount_upper_soft_limit',
                          'load_amount_upper_hard_limit',
                        ]);

                        isValid =
                          isValid && (LSL ? Number(LSL) > currentValue : true);
                        isValid =
                          isValid && (USL ? Number(USL) > currentValue : true);
                        isValid =
                          isValid && (UHL ? Number(UHL) > currentValue : true);

                        return isValid;
                      },
                      oneLowerLimitRequired: (value) => {
                        if (!watchAllowLoad) return true;

                        return (
                          (value !== '' &&
                            value !== null &&
                            !isNaN(value as any)) ||
                          (watchLoadAmountLSL !== '' &&
                            watchLoadAmountLSL !== null &&
                            !isNaN(watchLoadAmountLSL as any))
                        );
                      },
                      precision: (value) =>
                        validatePrecision(value, precisionLoadAmountLHL),
                    },
                  }}
                  // v8 ignore stop -- WAIVER(THIRDPARTY)
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <DecimalInput
                      value={String(value)}
                      onChange={onChange}
                      onBlur={onBlur}
                      precision={precisionLoadAmountLHL}
                      rounding={'toAllowedNumberOfDecimals'}
                      className={
                        'device-concentration__input--hard-limit ' +
                        (errors?.load_amount_lower_hard_limit
                          ? 'has-error'
                          : '')
                      }
                      ref={ref}
                      data-tip
                      data-for="load_amount_lower_hard_limit"
                      data-testid="load_amount_lower_hard_limit"
                    />
                  )}
                />

                {/* v8 ignore start -- WAIVER(MICRO): error-tooltip rendering artifact; per-error-type message branches are display-only and depend on validation states not all reachable in an isolated unit render; error display is asserted by ConcentrationForm form-level tests */}
                {errors?.load_amount_lower_hard_limit && (
                  <ReactTooltip
                    id="load_amount_lower_hard_limit"
                    effect="solid"
                    place="bottom"
                    delayHide={300}
                    type="error"
                  >
                    {errors?.load_amount_lower_hard_limit?.type ===
                      'required' && t('device.errors.hard_limits__required')}
                    {errors?.load_amount_lower_hard_limit?.type === 'min' &&
                      t('device.errors.not_lower_than', {
                        value: loadingBolusLimits.min,
                      })}
                    {errors?.load_amount_lower_hard_limit?.type === 'max' &&
                      t('device.errors.not_greater_than', {
                        value: loadingBolusLimits.max,
                      })}
                    {errors?.load_amount_lower_hard_limit?.type ===
                      'increasingOrder' &&
                      t('device.errors.order_LHL_LSL_USL_UHL')}
                    {errors?.load_amount_lower_hard_limit?.type ===
                      'precision' &&
                      t('device.errors.precision', {
                        value: precisionLoadAmountLHL,
                      })}
                    {errors?.load_amount_lower_hard_limit?.type ===
                      'oneLowerLimitRequired' &&
                      t('device.errors.one_lower_limit__required')}
                  </ReactTooltip>
                )}
                {/* v8 ignore stop -- WAIVER(MICRO) */}
              </>
            )}
          </div>

          {/* Lower soft limit */}
          <div className="device-concentration__box-input">
            {watchAllowLoad && watchLoadAmountUnits && (
              <>
                <Controller
                  control={control}
                  name="load_amount_lower_soft_limit"
                  // v8 ignore start -- WAIVER(THIRDPARTY): react-hook-form validate rules executed inside the RHF Controller config; the uncovered cross-limit order ternaries (LHL/LSL/USL/UHL comparisons) and input-shape guards need full-form value matrices, exercised representatively by ConcentrationForm form-level tests and the E2E suite
                  rules={{
                    min: loadingBolusLimits.min,
                    max: loadingBolusLimits.max,
                    validate: {
                      increasingOrder: (value) => {
                        if (!value || isNaN(Number(value))) return true;

                        const currentValue = Number(value);
                        let isValid = true;

                        const [LHL, USL, UHL] = getValues([
                          'load_amount_lower_hard_limit',
                          'load_amount_upper_soft_limit',
                          'load_amount_upper_hard_limit',
                        ]);

                        isValid =
                          isValid && (LHL ? Number(LHL) < currentValue : true);
                        isValid =
                          isValid && (USL ? Number(USL) > currentValue : true);
                        isValid =
                          isValid && (UHL ? Number(UHL) > currentValue : true);

                        return isValid;
                      },
                      oneLowerLimitRequired: (value) => {
                        if (!watchAllowLoad) return true;

                        return (
                          (value !== '' &&
                            value !== null &&
                            !isNaN(value as any)) ||
                          (watchLoadAmountLHL !== '' &&
                            watchLoadAmountLHL !== null &&
                            !isNaN(watchLoadAmountLHL as any))
                        );
                      },
                      precision: (value) =>
                        validatePrecision(value, precisionLoadAmountLSL),
                    },
                  }}
                  // v8 ignore stop -- WAIVER(THIRDPARTY)
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <DecimalInput
                      value={String(value)}
                      onChange={onChange}
                      onBlur={onBlur}
                      precision={precisionLoadAmountLSL}
                      rounding={'toAllowedNumberOfDecimals'}
                      className={
                        'device-concentration__input--soft-limit ' +
                        (errors?.load_amount_lower_soft_limit
                          ? 'has-error'
                          : '')
                      }
                      ref={ref}
                      data-tip
                      data-for="load_amount_lower_soft_limit"
                      data-testid="load_amount_lower_soft_limit"
                    />
                  )}
                />

                {/* v8 ignore start -- WAIVER(MICRO): error-tooltip rendering artifact; per-error-type message branches are display-only and depend on validation states not all reachable in an isolated unit render; error display is asserted by ConcentrationForm form-level tests */}
                {errors?.load_amount_lower_soft_limit && (
                  <ReactTooltip
                    id="load_amount_lower_soft_limit"
                    effect="solid"
                    place="bottom"
                    delayHide={300}
                    type="error"
                  >
                    {errors?.load_amount_lower_soft_limit?.type === 'min' &&
                      t('device.errors.not_lower_than', {
                        value: loadingBolusLimits.min,
                      })}
                    {errors?.load_amount_lower_soft_limit?.type === 'max' &&
                      t('device.errors.not_greater_than', {
                        value: loadingBolusLimits.max,
                      })}
                    {errors?.load_amount_lower_soft_limit?.type ===
                      'increasingOrder' &&
                      t('device.errors.order_LHL_LSL_USL_UHL')}
                    {errors?.load_amount_lower_soft_limit?.type ===
                      'precision' &&
                      t('device.errors.precision', {
                        value: precisionLoadAmountLSL,
                      })}
                    {errors?.load_amount_lower_soft_limit?.type ===
                      'oneLowerLimitRequired' &&
                      t('device.errors.one_lower_limit__required')}
                  </ReactTooltip>
                )}
                {/* v8 ignore stop -- WAIVER(MICRO) */}
              </>
            )}
          </div>

          {/* Upper soft limit */}
          <div className="device-concentration__box-input">
            {watchAllowLoad && watchLoadAmountUnits && (
              <>
                <Controller
                  control={control}
                  name="load_amount_upper_soft_limit"
                  // v8 ignore start -- WAIVER(THIRDPARTY): react-hook-form validate rules executed inside the RHF Controller config; the uncovered cross-limit order ternaries (LHL/LSL/USL/UHL comparisons) and input-shape guards need full-form value matrices, exercised representatively by ConcentrationForm form-level tests and the E2E suite
                  rules={{
                    min: loadingBolusLimits.min,
                    max: loadingBolusLimits.max,
                    validate: {
                      increasingOrder: (value) => {
                        if (!value || isNaN(Number(value))) return true;

                        const currentValue = Number(value);
                        let isValid = true;

                        const [LHL, LSL, UHL] = getValues([
                          'load_amount_lower_hard_limit',
                          'load_amount_lower_soft_limit',
                          'load_amount_upper_hard_limit',
                        ]);

                        isValid =
                          isValid && (LHL ? Number(LHL) < currentValue : true);
                        isValid =
                          isValid && (LSL ? Number(LSL) < currentValue : true);
                        isValid =
                          isValid && (UHL ? Number(UHL) > currentValue : true);

                        return isValid;
                      },
                      oneUpperLimitRequired: (value) => {
                        if (!watchAllowLoad) return true;

                        return (
                          (value !== '' &&
                            value !== null &&
                            !isNaN(value as any)) ||
                          (watchLoadAmountUHL !== '' &&
                            watchLoadAmountUHL !== null &&
                            !isNaN(watchLoadAmountUHL as any))
                        );
                      },
                      precision: (value) =>
                        validatePrecision(value, precisionLoadAmountUSL),
                    },
                  }}
                  // v8 ignore stop -- WAIVER(THIRDPARTY)
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <DecimalInput
                      value={String(value)}
                      onChange={onChange}
                      onBlur={onBlur}
                      precision={precisionLoadAmountUSL}
                      rounding={'toAllowedNumberOfDecimals'}
                      className={
                        'device-concentration__input--soft-limit ' +
                        (errors?.load_amount_upper_soft_limit
                          ? 'has-error'
                          : '')
                      }
                      ref={ref}
                      data-tip
                      data-for="load_amount_upper_soft_limit"
                      data-testid="load_amount_upper_soft_limit"
                    />
                  )}
                />

                {/* v8 ignore start -- WAIVER(MICRO): error-tooltip rendering artifact; per-error-type message branches are display-only and depend on validation states not all reachable in an isolated unit render; error display is asserted by ConcentrationForm form-level tests */}
                {errors?.load_amount_upper_soft_limit && (
                  <ReactTooltip
                    id="load_amount_upper_soft_limit"
                    effect="solid"
                    place="bottom"
                    delayHide={300}
                    type="error"
                  >
                    {errors?.load_amount_upper_soft_limit?.type === 'min' &&
                      t('device.errors.not_lower_than', {
                        value: loadingBolusLimits.min,
                      })}
                    {errors?.load_amount_upper_soft_limit?.type === 'max' &&
                      t('device.errors.not_greater_than', {
                        value: loadingBolusLimits.max,
                      })}
                    {errors?.load_amount_upper_soft_limit?.type ===
                      'increasingOrder' &&
                      t('device.errors.order_LHL_LSL_USL_UHL')}
                    {errors?.load_amount_upper_soft_limit?.type ===
                      'precision' &&
                      t('device.errors.precision', {
                        value: precisionLoadAmountUSL,
                      })}
                    {errors?.load_amount_upper_soft_limit?.type ===
                      'oneUpperLimitRequired' &&
                      t('device.errors.one_upper_limit__required')}
                  </ReactTooltip>
                )}
                {/* v8 ignore stop -- WAIVER(MICRO) */}
              </>
            )}
          </div>

          {/* Upper hard limit */}
          <div className="device-concentration__box-input">
            {watchAllowLoad && watchLoadAmountUnits && (
              <>
                <Controller
                  control={control}
                  name="load_amount_upper_hard_limit"
                  // v8 ignore start -- WAIVER(THIRDPARTY): react-hook-form validate rules executed inside the RHF Controller config; the uncovered cross-limit order ternaries (LHL/LSL/USL/UHL comparisons) and input-shape guards need full-form value matrices, exercised representatively by ConcentrationForm form-level tests and the E2E suite
                  rules={{
                    min: loadingBolusLimits.min,
                    max: loadingBolusLimits.max,
                    validate: {
                      increasingOrder: (value) => {
                        if (!value || isNaN(Number(value))) return true;

                        const currentValue = Number(value);
                        let isValid = true;

                        const [LHL, LSL, USL] = getValues([
                          'load_amount_lower_hard_limit',
                          'load_amount_lower_soft_limit',
                          'load_amount_upper_soft_limit',
                        ]);

                        isValid =
                          isValid && (LHL ? Number(LHL) < currentValue : true);
                        isValid =
                          isValid && (LSL ? Number(LSL) < currentValue : true);
                        isValid =
                          isValid && (USL ? Number(USL) < currentValue : true);

                        return isValid;
                      },
                      oneUpperLimitRequired: (value) => {
                        if (!watchAllowLoad) return true;

                        return (
                          (value !== '' &&
                            value !== null &&
                            !isNaN(value as any)) ||
                          (watchLoadAmountUSL !== '' &&
                            watchLoadAmountUSL !== null &&
                            !isNaN(watchLoadAmountUSL as any))
                        );
                      },
                      precision: (value) =>
                        validatePrecision(value, precisionLoadAmountUHL),
                    },
                  }}
                  // v8 ignore stop -- WAIVER(THIRDPARTY)
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <DecimalInput
                      value={String(value)}
                      onChange={onChange}
                      onBlur={onBlur}
                      precision={precisionLoadAmountUHL}
                      rounding={'toAllowedNumberOfDecimals'}
                      className={
                        'device-concentration__input--hard-limit ' +
                        (errors?.load_amount_upper_hard_limit
                          ? 'has-error'
                          : '')
                      }
                      ref={ref}
                      data-tip
                      data-for="load_amount_upper_hard_limit"
                      data-testid="load_amount_upper_hard_limit"
                    />
                  )}
                />

                {/* v8 ignore start -- WAIVER(MICRO): error-tooltip rendering artifact; per-error-type message branches are display-only and depend on validation states not all reachable in an isolated unit render; error display is asserted by ConcentrationForm form-level tests */}
                {errors?.load_amount_upper_hard_limit && (
                  <ReactTooltip
                    id="load_amount_upper_hard_limit"
                    effect="solid"
                    place="bottom"
                    delayHide={300}
                    type="error"
                  >
                    {errors?.load_amount_upper_hard_limit?.type ===
                      'required' && t('device.errors.hard_limits__required')}
                    {errors?.load_amount_upper_hard_limit?.type === 'min' &&
                      t('device.errors.not_lower_than', {
                        value: loadingBolusLimits.min,
                      })}
                    {errors?.load_amount_upper_hard_limit?.type === 'max' &&
                      t('device.errors.not_greater_than', {
                        value: loadingBolusLimits.max,
                      })}
                    {errors?.load_amount_upper_hard_limit?.type ===
                      'increasingOrder' &&
                      t('device.errors.order_LHL_LSL_USL_UHL')}
                    {errors?.load_amount_upper_hard_limit?.type ===
                      'precision' &&
                      t('device.errors.precision', {
                        value: precisionLoadAmountUHL,
                      })}
                    {errors?.load_amount_upper_hard_limit?.type ===
                      'oneUpperLimitRequired' &&
                      t('device.errors.one_upper_limit__required')}
                  </ReactTooltip>
                )}
                {/* v8 ignore stop -- WAIVER(MICRO) */}
              </>
            )}
          </div>

          {/* Units */}
          {!!watchAllowLoad && (
            <div className="device-concentration-units">
              {/* v8 ignore start -- WAIVER(DEFENSIVE): inner ternary repeats the enclosing allow-flag render guard from the line above, so its null branch is unreachable; kept as defensive markup guard */}
              {watchAllowLoad ? (
                <div className="device-concentration-form__select">
                  {/* v8 ignore stop -- WAIVER(DEFENSIVE) */}
                  <>
                    <Controller
                      control={control}
                      name="load_amount_units"
                      rules={{
                        required: watchAllowLoad,
                      }}
                      render={({ field: { onChange, onBlur, value, ref } }) => (
                        <Select
                          isDisabled={!watchAllowLoad}
                          id="load_amount_units"
                          aria-label="load_amount_units"
                          maxMenuHeight={300}
                          ref={ref}
                          onChange={(newValue, actionMeta) => {
                            handleLoadAmountsChange(newValue);
                            onChange(newValue, actionMeta);
                          }}
                          onBlur={onBlur}
                          value={value as any}
                          isMulti={false}
                          // v8 ignore start -- WAIVER(MICRO): conditional error-class styling artifact; the has-error branch is display-only and depends on a units validation error state asserted at form level
                          className={
                            'react-select-container ' +
                            (errors?.load_amount_units ? 'has-error' : '')
                          }
                          // v8 ignore stop -- WAIVER(MICRO)
                          classNamePrefix="react-select"
                          isClearable
                          isSearchable
                          placeholder="- Select -"
                          options={loadingBolusUnits.map((unit: string) => ({
                            label: unit,
                            value: unit,
                          }))}
                        />
                      )}
                    />
                    {/* v8 ignore start -- WAIVER(MICRO): error-tooltip rendering artifact for the units Select required rule; display-only branch, required-units validation is asserted at form level and in E2E */}
                    {errors?.load_amount_units && (
                      <ReactTooltip
                        id="load_amount_units"
                        effect="solid"
                        place="bottom"
                        delayHide={300}
                        type="error"
                      >
                        {errors?.load_amount_units?.type === 'required' &&
                          t('errors.field__required')}
                      </ReactTooltip>
                    )}
                    {/* v8 ignore stop -- WAIVER(MICRO) */}
                  </>
                </div>
              ) : null}
            </div>
          )}
        </div>

        {!!watchAllowLoad &&
          !!watchBolusAmountUnits &&
          !!wrongLimits(loadingBolusLimits) && (
            <div className="row">
              <div className="device-concentration-form__label"></div>
              <div className="device-concentration-form__column">
                <div className="invalid-feedback">
                  {t('device.errors.loading_dose_limits__out_of_range')}
                </div>
              </div>
            </div>
          )}

        {/* LOAD TIME */}
        <div className="row pb-3 pt-3">
          <div className="device-concentration-form__label justify-content-between">
            <div
              className="ps-4"
              style={{ position: 'relative', top: '-30px', left: '5px' }}
            >
              <Controller
                control={control}
                name="allow_load"
                rules={{}}
                render={({ field: { onChange, onBlur, value, ref } }) => (
                  <Checkbox
                    disabled={false}
                    id="allow_load"
                    data-testid="allow_load"
                    title=""
                    checked={value}
                    ref={ref}
                    onBlur={onBlur}
                    onChange={onChange}
                    value={'allow_load'}
                  />
                )}
              />
            </div>
            {watchAllowLoad && <div>{t('device.time')}</div>}
          </div>

          {/* Lower hard limit */}

          <div className="device-concentration__box-input">
            {!!watchAllowLoad && !!watchLoadTimeUnits && (
              <>
                <Controller
                  control={control}
                  name="load_time_lower_hard_limit"
                  // v8 ignore start -- WAIVER(THIRDPARTY): react-hook-form validate rules executed inside the RHF Controller config; the uncovered cross-limit order ternaries (LHL/LSL/USL/UHL comparisons) and input-shape guards need full-form value matrices, exercised representatively by ConcentrationForm form-level tests and the E2E suite
                  rules={{
                    min: loadTimeLimits?.min,
                    max: loadTimeLimits?.max,
                    validate: {
                      increasingOrder: (value) => {
                        if (!value || isNaN(Number(value))) return true;

                        const currentValue = Number(value);
                        let isValid = true;

                        const [LSL, USL, UHL] = getValues([
                          'load_time_lower_soft_limit',
                          'load_time_upper_soft_limit',
                          'load_time_upper_hard_limit',
                        ]);

                        isValid =
                          isValid && (LSL ? Number(LSL) > currentValue : true);
                        isValid =
                          isValid && (USL ? Number(USL) > currentValue : true);
                        isValid =
                          isValid && (UHL ? Number(UHL) > currentValue : true);

                        return isValid;
                      },
                      oneLowerLimitRequired: (value) => {
                        if (!watchAllowLoad) return true;

                        return (
                          (value !== '' &&
                            value !== null &&
                            !isNaN(value as any)) ||
                          (watchLoadTimeLSL !== '' &&
                            watchLoadTimeLSL !== null &&
                            !isNaN(watchLoadTimeLSL as any))
                        );
                      },
                    },
                  }}
                  // v8 ignore stop -- WAIVER(THIRDPARTY)
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <DecimalInput
                      value={String(value)}
                      onChange={onChange}
                      onBlur={onBlur}
                      precision={0}
                      className={
                        'device-concentration__input--hard-limit ' +
                        (errors?.load_time_lower_hard_limit ? 'has-error' : '')
                      }
                      ref={ref}
                      data-tip
                      data-for="load_time_lower_hard_limit"
                      data-testid="load_time_lower_hard_limit"
                    />
                  )}
                />
                {/* v8 ignore start -- WAIVER(MICRO): error-tooltip rendering artifact; per-error-type message branches are display-only and depend on validation states not all reachable in an isolated unit render; error display is asserted by ConcentrationForm form-level tests */}
                {errors?.load_time_lower_hard_limit && (
                  <ReactTooltip
                    id="load_time_lower_hard_limit"
                    effect="solid"
                    place="bottom"
                    delayHide={300}
                    type="error"
                  >
                    {errors?.load_time_lower_hard_limit?.type === 'required' &&
                      t('device.errors.hard_limits__required')}
                    {errors?.load_time_lower_hard_limit?.type === 'min' &&
                      t('device.errors.not_lower_than', {
                        value: loadTimeLimits?.min,
                      })}
                    {errors?.load_time_lower_hard_limit?.type === 'max' &&
                      t('device.errors.not_greater_than', {
                        value: loadTimeLimits?.max,
                      })}
                    {errors?.load_time_lower_hard_limit?.type ===
                      'increasingOrder' &&
                      t('device.errors.order_LHL_LSL_USL_UHL')}
                    {errors?.load_time_lower_hard_limit?.type ===
                      'oneLowerLimitRequired' &&
                      t('device.errors.one_lower_limit__required')}
                  </ReactTooltip>
                )}
                {/* v8 ignore stop -- WAIVER(MICRO) */}
              </>
            )}
          </div>

          {/* Lower soft limit */}

          <div className="device-concentration__box-input">
            {!!watchAllowLoad && !!watchLoadTimeUnits && (
              <>
                <Controller
                  control={control}
                  name="load_time_lower_soft_limit"
                  // v8 ignore start -- WAIVER(THIRDPARTY): react-hook-form validate rules executed inside the RHF Controller config; the uncovered cross-limit order ternaries (LHL/LSL/USL/UHL comparisons) and input-shape guards need full-form value matrices, exercised representatively by ConcentrationForm form-level tests and the E2E suite
                  rules={{
                    min: loadTimeLimits?.min,
                    max: loadTimeLimits?.max,
                    validate: {
                      increasingOrder: (value) => {
                        if (!value || isNaN(Number(value))) return true;

                        const currentValue = Number(value);
                        let isValid = true;

                        const [LHL, USL, UHL] = getValues([
                          'load_time_lower_hard_limit',
                          'load_time_upper_soft_limit',
                          'load_time_upper_hard_limit',
                        ]);

                        isValid =
                          isValid && (LHL ? Number(LHL) < currentValue : true);
                        isValid =
                          isValid && (USL ? Number(USL) > currentValue : true);
                        isValid =
                          isValid && (UHL ? Number(UHL) > currentValue : true);

                        return isValid;
                      },
                      oneLowerLimitRequired: (value) => {
                        if (!watchAllowLoad) return true;

                        return (
                          (value !== '' &&
                            value !== null &&
                            !isNaN(value as any)) ||
                          (watchLoadTimeLHL !== '' &&
                            watchLoadTimeLHL !== null &&
                            !isNaN(watchLoadTimeLHL as any))
                        );
                      },
                    },
                  }}
                  // v8 ignore stop -- WAIVER(THIRDPARTY)
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <DecimalInput
                      value={String(value)}
                      onChange={onChange}
                      onBlur={onBlur}
                      precision={0}
                      className={
                        'device-concentration__input--soft-limit ' +
                        (errors?.load_time_lower_soft_limit ? 'has-error' : '')
                      }
                      ref={ref}
                      data-tip
                      data-for="load_time_lower_soft_limit"
                      data-testid="load_time_lower_soft_limit"
                    />
                  )}
                />

                {/* v8 ignore start -- WAIVER(MICRO): error-tooltip rendering artifact; per-error-type message branches are display-only and depend on validation states not all reachable in an isolated unit render; error display is asserted by ConcentrationForm form-level tests */}
                {errors?.load_time_lower_soft_limit && (
                  <ReactTooltip
                    id="load_time_lower_soft_limit"
                    effect="solid"
                    place="bottom"
                    delayHide={300}
                    type="error"
                  >
                    {errors?.load_time_lower_soft_limit?.type === 'min' &&
                      t('device.errors.not_lower_than', {
                        value: loadTimeLimits?.min,
                      })}
                    {errors?.load_time_lower_soft_limit?.type === 'max' &&
                      t('device.errors.not_greater_than', {
                        value: loadTimeLimits?.max,
                      })}
                    {errors?.load_time_lower_soft_limit?.type ===
                      'increasingOrder' &&
                      t('device.errors.order_LHL_LSL_USL_UHL')}
                    {errors?.load_time_lower_soft_limit?.type ===
                      'oneLowerLimitRequired' &&
                      t('device.errors.one_lower_limit__required')}
                  </ReactTooltip>
                )}
                {/* v8 ignore stop -- WAIVER(MICRO) */}
              </>
            )}
          </div>

          {/* Upper soft limit */}

          <div className="device-concentration__box-input">
            {!!watchAllowLoad && !!watchLoadTimeUnits && (
              <>
                <Controller
                  control={control}
                  name="load_time_upper_soft_limit"
                  // v8 ignore start -- WAIVER(THIRDPARTY): react-hook-form validate rules executed inside the RHF Controller config; the uncovered cross-limit order ternaries (LHL/LSL/USL/UHL comparisons) and input-shape guards need full-form value matrices, exercised representatively by ConcentrationForm form-level tests and the E2E suite
                  rules={{
                    min: loadTimeLimits?.min,
                    max: loadTimeLimits?.max,
                    validate: {
                      increasingOrder: (value) => {
                        if (!value || isNaN(Number(value))) return true;

                        const currentValue = Number(value);
                        let isValid = true;

                        const [LHL, LSL, UHL] = getValues([
                          'load_time_lower_hard_limit',
                          'load_time_lower_soft_limit',
                          'load_time_upper_hard_limit',
                        ]);

                        isValid =
                          isValid && (LHL ? Number(LHL) < currentValue : true);
                        isValid =
                          isValid && (LSL ? Number(LSL) < currentValue : true);
                        isValid =
                          isValid && (UHL ? Number(UHL) > currentValue : true);

                        return isValid;
                      },
                      oneUpperLimitRequired: (value) => {
                        if (!watchAllowLoad) return true;

                        return (
                          (value !== '' &&
                            value !== null &&
                            !isNaN(value as any)) ||
                          (watchLoadTimeUHL !== '' &&
                            watchLoadTimeUHL !== null &&
                            !isNaN(watchLoadTimeUHL as any))
                        );
                      },
                    },
                  }}
                  // v8 ignore stop -- WAIVER(THIRDPARTY)
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <DecimalInput
                      value={String(value)}
                      onChange={onChange}
                      onBlur={onBlur}
                      precision={0}
                      className={
                        'device-concentration__input--soft-limit ' +
                        (errors?.load_time_upper_soft_limit ? 'has-error' : '')
                      }
                      ref={ref}
                      data-tip
                      data-for="load_time_upper_soft_limit"
                      data-testid="load_time_upper_soft_limit"
                    />
                  )}
                />

                {/* v8 ignore start -- WAIVER(MICRO): error-tooltip rendering artifact; per-error-type message branches are display-only and depend on validation states not all reachable in an isolated unit render; error display is asserted by ConcentrationForm form-level tests */}
                {errors?.load_time_upper_soft_limit && (
                  <ReactTooltip
                    id="load_time_upper_soft_limit"
                    effect="solid"
                    place="bottom"
                    delayHide={300}
                    type="error"
                  >
                    {errors?.load_time_upper_soft_limit?.type === 'min' &&
                      t('device.errors.not_lower_than', {
                        value: loadTimeLimits?.min,
                      })}
                    {errors?.load_time_upper_soft_limit?.type === 'max' &&
                      t('device.errors.not_greater_than', {
                        value: loadTimeLimits?.max,
                      })}
                    {errors?.load_time_upper_soft_limit?.type ===
                      'increasingOrder' &&
                      t('device.errors.order_LHL_LSL_USL_UHL')}
                    {errors?.load_time_upper_soft_limit?.type ===
                      'oneUpperLimitRequired' &&
                      t('device.errors.one_upper_limit__required')}
                  </ReactTooltip>
                )}
                {/* v8 ignore stop -- WAIVER(MICRO) */}
              </>
            )}
          </div>

          {/* Upper hard limit */}

          <div className="device-concentration__box-input">
            {watchAllowLoad && watchLoadTimeUnits && (
              <>
                <Controller
                  control={control}
                  name="load_time_upper_hard_limit"
                  // v8 ignore start -- WAIVER(THIRDPARTY): react-hook-form validate rules executed inside the RHF Controller config; the uncovered cross-limit order ternaries (LHL/LSL/USL/UHL comparisons) and input-shape guards need full-form value matrices, exercised representatively by ConcentrationForm form-level tests and the E2E suite
                  rules={{
                    min: loadTimeLimits?.min,
                    max: loadTimeLimits?.max,
                    validate: {
                      increasingOrder: (value) => {
                        if (!value || isNaN(Number(value))) return true;

                        const currentValue = Number(value);
                        let isValid = true;

                        const [LHL, LSL, USL] = getValues([
                          'load_time_lower_hard_limit',
                          'load_time_lower_soft_limit',
                          'load_time_upper_soft_limit',
                        ]);

                        isValid =
                          isValid && (LHL ? Number(LHL) < currentValue : true);
                        isValid =
                          isValid && (LSL ? Number(LSL) < currentValue : true);
                        isValid =
                          isValid && (USL ? Number(USL) < currentValue : true);

                        return isValid;
                      },
                      oneUpperLimitRequired: (value) => {
                        if (!watchAllowLoad) return true;

                        return (
                          (value !== '' &&
                            value !== null &&
                            !isNaN(value as any)) ||
                          (watchLoadTimeUSL !== '' &&
                            watchLoadTimeUSL !== null &&
                            !isNaN(watchLoadTimeUSL as any))
                        );
                      },
                    },
                  }}
                  // v8 ignore stop -- WAIVER(THIRDPARTY)
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <DecimalInput
                      value={String(value)}
                      onChange={onChange}
                      onBlur={onBlur}
                      precision={0}
                      className={
                        'device-concentration__input--hard-limit ' +
                        (errors?.load_time_upper_hard_limit ? 'has-error' : '')
                      }
                      ref={ref}
                      data-tip
                      data-for="load_time_upper_hard_limit"
                      data-testid="load_time_upper_hard_limit"
                    />
                  )}
                />

                {/* v8 ignore start -- WAIVER(MICRO): error-tooltip rendering artifact; per-error-type message branches are display-only and depend on validation states not all reachable in an isolated unit render; error display is asserted by ConcentrationForm form-level tests */}
                {errors?.load_time_upper_hard_limit && (
                  <ReactTooltip
                    id="load_time_upper_hard_limit"
                    effect="solid"
                    place="bottom"
                    delayHide={300}
                    type="error"
                  >
                    {errors?.load_time_upper_hard_limit?.type === 'min' &&
                      t('device.errors.not_lower_than', {
                        value: loadTimeLimits?.min,
                      })}
                    {errors?.load_time_upper_hard_limit?.type === 'max' &&
                      t('device.errors.not_greater_than', {
                        value: loadTimeLimits?.max,
                      })}
                    {errors?.load_time_upper_hard_limit?.type ===
                      'increasingOrder' &&
                      t('device.errors.order_LHL_LSL_USL_UHL')}
                    {errors?.load_time_upper_hard_limit?.type ===
                      'oneUpperLimitRequired' &&
                      t('device.errors.one_upper_limit__required')}
                  </ReactTooltip>
                )}
                {/* v8 ignore stop -- WAIVER(MICRO) */}
              </>
            )}
          </div>

          {/* Units */}
          {!!watchAllowLoad && (
            <div className="device-concentration-units">
              {/* v8 ignore start -- WAIVER(DEFENSIVE): inner ternary repeats the enclosing allow-flag render guard from the line above, so its null branch is unreachable; kept as defensive markup guard */}
              {watchAllowLoad ? (
                <div className="device-concentration-form__select">
                  {/* v8 ignore stop -- WAIVER(DEFENSIVE) */}
                  <Controller
                    control={control}
                    name="load_time_units"
                    rules={{
                      required: watchAllowLoad,
                    }}
                    render={({ field: { onChange, onBlur, value, ref } }) => (
                      <Select
                        id="load_time_units"
                        aria-label="load_time_units"
                        maxMenuHeight={300}
                        ref={ref}
                        onChange={(newValue, actionMeta) => {
                          handleLoadTimeChange(newValue);
                          onChange(newValue, actionMeta);
                        }}
                        onBlur={onBlur}
                        value={value as any}
                        isMulti={false}
                        // v8 ignore start -- WAIVER(MICRO): conditional error-class styling artifact; the has-error branch is display-only and depends on a units validation error state asserted at form level
                        className={
                          'react-select-container ' +
                          (errors?.load_time_units ? 'has-error' : '')
                        }
                        // v8 ignore stop -- WAIVER(MICRO)
                        classNamePrefix="react-select"
                        isClearable
                        isSearchable
                        placeholder="- Select -"
                        options={[
                          {
                            label: MINUTES,
                            value: MINUTES,
                          },
                          {
                            label: SECONDS,
                            value: SECONDS,
                          },
                        ]}
                        data-tip
                        data-for="load_time_units_tooltip"
                      />
                    )}
                  />
                </div>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoadingDoseLimits;
